import { useQuery, useMutation } from "@apollo/client"

import {
  GetGlobalAlertTemplatesDocument,
  CreateGlobalAlertTemplateDocument,
  UpdateGlobalAlertTemplateDocument,
  GrantTemplateVisibilityDocument,
  RevokeTemplateVisibilityDocument,
} from "@hydrologiq/gql-experience/graphql"

export interface GlobalAlertTemplateAssignment {
  id: string
  tenantId: string
  tenantName: string
  assetId: string | null
  systemId: string | null
  enabled: boolean
  thresholdOverride: number | null
  timeWindowOverrideSeconds: number | null
  paramValues: Record<string, any> | null
  createdAt: string | null
}

export interface GlobalAlertTemplate {
  id: string
  name: string
  description: string | null
  metric: string | null
  conditionType: string | null
  operator: string | null
  threshold: number | null
  timeWindowSeconds: number | null
  severity: string
  evaluationTier: string
  scopeLevel: string | null
  overridable: boolean
  thresholdUnit: string | null
  thresholdDirection: string | null
  enabled: boolean
  createdAt: string | null
  updatedAt: string | null
  compositeConfig: Record<string, any> | null
  visibleToTenants: { tenantId: string; tenantName: string; grantedAt: string | null }[]
  assignmentCount: number
  assignments: GlobalAlertTemplateAssignment[]
}

export function useAlertTemplateAdmin() {
  const { data, loading, error, refetch } = useQuery(
    GetGlobalAlertTemplatesDocument,
    { fetchPolicy: "cache-and-network" }
  )

  const [createTemplateMutation, { loading: creating }] = useMutation(
    CreateGlobalAlertTemplateDocument
  )

  const [updateTemplateMutation, { loading: updating }] = useMutation(
    UpdateGlobalAlertTemplateDocument
  )

  const [grantVisibilityMutation, { loading: granting }] = useMutation(
    GrantTemplateVisibilityDocument
  )

  const [revokeVisibilityMutation, { loading: revoking }] = useMutation(
    RevokeTemplateVisibilityDocument
  )

  const templates: GlobalAlertTemplate[] = (data?.globalAlertTemplates ?? []) as GlobalAlertTemplate[]

  const createTemplate = async (input: any) => {
    const result = await createTemplateMutation({ variables: { input } })
    if (result.errors?.length) throw new Error(result.errors[0].message)
    await refetch()
    return result.data?.createGlobalAlertTemplate?.template
  }

  const updateTemplate = async (id: string, input: any) => {
    const result = await updateTemplateMutation({ variables: { id, input } })
    if (result.errors?.length) throw new Error(result.errors[0].message)
    await refetch()
    return result.data?.updateGlobalAlertTemplate?.template
  }

  const grantVisibility = async (templateId: string, tenantIds: string[]) => {
    const result = await grantVisibilityMutation({ variables: { templateId, tenantIds } })
    if (result.errors?.length) throw new Error(result.errors[0].message)
    await refetch()
    return result.data?.grantTemplateVisibility?.granted
  }

  const revokeVisibility = async (templateId: string, tenantIds: string[]) => {
    const result = await revokeVisibilityMutation({ variables: { templateId, tenantIds } })
    if (result.errors?.length) throw new Error(result.errors[0].message)
    await refetch()
    return result.data?.revokeTemplateVisibility
  }

  return {
    templates,
    loading,
    error,
    refetch,
    createTemplate,
    updateTemplate,
    grantVisibility,
    revokeVisibility,
    creating,
    updating,
    granting,
    revoking,
  }
}
